import { useCallback } from 'react';

import {
  Appointment,
  AttributionDetails,
  OccupancyType,
} from '@generated/gql/graphql';
import { useUserInformation } from '@hooks/useUserInformation';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { useHostedAttribution } from '@providers/state/HostedAttributionProvider';
import { UserInformation } from '@src/types/auth';
import { useTrackingContext } from '@tracking/Provider/Context';
import {
  SignupApplicationEntity,
  signupApplicationEntity,
} from '@tracking/trackingEvents';
import { buildOptimizelyEntity } from '@tracking/utils/buildOptimizelyEntity';
import {
  parseAttributionContext,
  parseSessionContext,
} from '@tracking/utils/parseAttributionContext';
import { isNullish } from '@utils/isNullish';

const getApplicationTrackingContext = (
  basketId: string,
  postcode: string,
  homeOwnerSelection: Nullable<OccupancyType>,
  appointment: Nullable<Appointment>,
  createdAt: string,
  userInformation: Nullable<UserInformation>,
  isHostedLink: boolean,
  isPropertyAlreadySupplied: Nullable<boolean>
): SignupApplicationEntity => {
  const isHomeOwner = isNullish(homeOwnerSelection)
    ? undefined
    : homeOwnerSelection === OccupancyType.Homeowner;

  const sessionContext = parseSessionContext(appointment, userInformation);

  return {
    created_at: createdAt,
    basket_id: basketId,
    supply_postcode: postcode,
    is_home_owner: isHomeOwner,
    is_hosted_link: isHostedLink,
    is_property_already_supplied: isPropertyAlreadySupplied ?? undefined,
    ...sessionContext,
  };
};

const useAddressTracking = () => {
  const userInformation = useUserInformation();
  const { handleCreateBasket } = useTrackingContext();
  const { appointment } = useAppointmentContext();
  const { hostedId } = useHostedAttribution();
  const isHostedLink = Boolean(hostedId);

  const buildTrackingContexts = useCallback(
    (
      basketId: string,
      postcode: string,
      occupancyType: Nullable<OccupancyType>,
      createdAt: string,
      attribution: Nullable<AttributionDetails>,
      isPropertyAlreadySupplied: Nullable<boolean>
    ) => {
      const attributionContext = parseAttributionContext(attribution);
      const applicationContext = getApplicationTrackingContext(
        basketId,
        postcode,
        occupancyType,
        appointment,
        createdAt,
        userInformation,
        isHostedLink,
        isPropertyAlreadySupplied
      );
      const fullContext = { ...applicationContext, ...attributionContext };

      handleCreateBasket(fullContext);

      return [signupApplicationEntity(fullContext), ...buildOptimizelyEntity()];
    },
    [handleCreateBasket, appointment, userInformation, isHostedLink]
  );

  return { buildTrackingContexts };
};

export { useAddressTracking, getApplicationTrackingContext };
